"use client"

import { useEffect, useMemo, useState } from "react"
import { ChevronLeft, Loader2 } from "lucide-react"
import {
  Switch,
  TagInput,
  TextArea,
  cn,
  toast,
} from "@strivelabs/ui-core"
import { Button } from "@strivelabs/ui-core/v2"
import type { TagInputItem } from "@strivelabs/ui-core"
import { log } from "@/lib/logger"
import { validateUrl } from "@/lib/utils"
import { manageKeyVal } from "@/services/DataService"
import { useTriggerWorkflow } from "@/hooks/useTriggerWorkflow"
import { useAppStore } from "@/store/appStore"
import { useNavigate } from "react-router-dom"
import {
  APP_NAME,
  KeyValEntityType,
  ROUTES,
} from "@/agents/blog-post-content-strategizer/constants"
import { ChoiceOptions } from "@/agents/blog-post-content-strategizer/components/stepper/ChoiceOptions"

export const NOTES_TO_AGENT_MAX_LENGTH = 500

export const outlineOptions = {
  DEFAULT_OUTLINE: "default_outline",
  PLP_OUTLINE: "plp_outline",
} as const

export type OutlineType =
  | typeof outlineOptions.DEFAULT_OUTLINE
  | typeof outlineOptions.PLP_OUTLINE

const outlineChoices = [
  {
    id: outlineOptions.DEFAULT_OUTLINE,
    name: "Blog post outline",
    description: "Standard SEO-first blog post structure",
  },
  {
    id: outlineOptions.PLP_OUTLINE,
    name: "Product listing page outline",
    description: "Outline tailored to a specific PLP URL",
  },
]

const intentArchetypeOptions = [
  {
    id: "informational",
    name: "Informational",
    description: "Reader is learning about this topic, not ready to buy",
  },
  {
    id: "navigational",
    name: "Navigational",
    description: "Reader already knows this brand and wants a specific page or resource",
  },
  {
    id: "commercial_investigation",
    name: "Commercial investigation",
    description: "Reader is comparing or evaluating options before deciding",
  },
  {
    id: "transactional",
    name: "Transactional",
    description: "Reader is ready to act — sign up, buy, or start using something",
  },
]

const contentDepthOptions = [
  { id: "overview", name: "Overview", description: "3–5 H2s, 800–1,300 words" },
  {
    id: "standard",
    name: "Standard",
    description: "5–7 H2s, 1,500–2,500 words",
  },
  {
    id: "comprehensive",
    name: "Comprehensive",
    description: "7–10 H2s, 2,500–4,000 words",
  },
]

const SLUG_MAX_LENGTH = 60
const UNIQUE_SUFFIX_LENGTH = 6

/**
 * Turns free text into a URL/id-safe slug: lowercase, non-alphanumerics
 * collapsed to single hyphens, trimmed of leading/trailing hyphens, capped
 * at SLUG_MAX_LENGTH so a long keyword phrase can't produce an unwieldy key.
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH)
}

/**
 * The new blog's key: derived from the first primary keyword so it's a
 * meaningful, human-recognizable identifier (shown as the blog's title
 * everywhere downstream until an outline exists) rather than an opaque
 * UUID, with a short random suffix appended so two submissions that happen
 * to share a keyword can never collide into the same sor_blog row — the
 * backend does a find-or-create by this exact key
 * (SORBlogEntity.claimForOutlineByKey), so an accidental collision would
 * silently attach a new submission's outline attempt to an unrelated
 * existing blog instead of creating a new one.
 */
export function buildBlogKey(primaryKeyword: string | undefined): string {
  const suffix = crypto.randomUUID().slice(0, UNIQUE_SUFFIX_LENGTH)
  const slug = primaryKeyword ? slugify(primaryKeyword) : ""
  return slug ? `${slug}-${suffix}` : crypto.randomUUID()
}

const inputClass = cn(
  "w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
  "ring-offset-background placeholder:text-muted-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
)

export default function TriggerScreen() {
  const [screen, setScreen] = useState<1 | 2>(1)
  const [outlineType, setOutlineType] = useState<OutlineType>(
    outlineOptions.DEFAULT_OUTLINE,
  )
  const [plpUrl, setPlpUrl] = useState("")
  const [primaryKeywords, setPrimaryKeywords] = useState<TagInputItem[]>([])
  const [secondaryKeywords, setSecondaryKeywords] = useState<TagInputItem[]>([])
  const [productName, setProductName] = useState("")
  const [contentFocusAngle, setContentFocusAngle] = useState("")
  const [competitorsMatching, setCompetitorsMatching] = useState(false)
  const [intentArchetype, setIntentArchetype] = useState("")
  const [contentDepth, setContentDepth] = useState("")
  const [notesToAgent, setNotesToAgent] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)

  const activeWorkflowInstance = useAppStore(
    (state) => state.activeWorkflowInstance,
  )
  const agentInstances = useAppStore((state) => state.agentInstances)
  const navigate = useNavigate()
  const { mutateAsync: triggerWorkflow } = useTriggerWorkflow()

  const agentConfig = useMemo(() => {
    const instance = agentInstances.find(
      (inst) => inst.agent?.short_name === "blog-post-content-strategizer",
    )
    return instance?.config as Record<string, unknown> | undefined
  }, [agentInstances])

  const showContentFocusAngle =
    agentConfig?.disable_content_focus_angle === false

  // Reset fields irrelevant to the chosen outline
  useEffect(() => {
    if (outlineType !== outlineOptions.PLP_OUTLINE) {
      setPlpUrl("")
    }
    if (outlineType === outlineOptions.PLP_OUTLINE) {
      setProductName("")
      setIntentArchetype("")
      setContentDepth("")
      setNotesToAgent("")
      setCompetitorsMatching(false)
    }
  }, [outlineType])

  // Reset fields covered by competitor matching
  useEffect(() => {
    if (competitorsMatching) {
      setIntentArchetype("")
      setContentDepth("")
      setNotesToAgent("")
    }
  }, [competitorsMatching])

  const isPlp = outlineType === outlineOptions.PLP_OUTLINE
  const isDefault = outlineType === outlineOptions.DEFAULT_OUTLINE

  const trimmedPlp = plpUrl.trim()
  const plpValid = trimmedPlp.length > 0 && validateUrl(trimmedPlp)
  const requiredValid = primaryKeywords.length > 0 && (!isPlp || plpValid)

  const validateRequired = (): boolean => {
    if (primaryKeywords.length === 0) {
      toast({
        title: "Missing primary keywords",
        description: "Please add at least one primary keyword",
        variant: "destructive",
      })
      return false
    }
    if (isPlp && trimmedPlp.length === 0) {
      toast({
        title: "Missing PLP URL",
        description:
          "Please provide the Product Listing Page URL for PLP outline type",
        variant: "destructive",
      })
      return false
    }
    if (isPlp && !validateUrl(trimmedPlp)) {
      toast({
        title: "Invalid PLP URL",
        description: "Please enter a valid Product Listing Page URL",
        variant: "destructive",
      })
      return false
    }
    return true
  }

  const handleContinue = () => {
    if (!validateRequired()) return
    setScreen(2)
  }

  const handleGoToOutput = () => navigate(ROUTES.OUTPUT)

  const handleSubmit = async () => {
    if (!validateRequired()) return

    if (!activeWorkflowInstance) {
      log("error", "No active workflow instance found")
      toast({
        title: "Error",
        description:
          "No active workflow instance found. Please try refreshing the page.",
        variant: "destructive",
      })
      return
    }

    const workflowInstanceId = activeWorkflowInstance?.id?.toString()
    if (!workflowInstanceId) {
      log("error", "Missing workflow instance id")
      toast({
        title: "Error",
        description:
          "Unable to determine workflow instance. Please try again.",
        variant: "destructive",
      })
      return
    }

    setIsProcessing(true)
    log("info", "Processing form data", {
      primary_count: primaryKeywords.length,
      secondary_count: secondaryKeywords.length,
      productName,
      contentFocusAngle,
    })

    const todoKey = buildBlogKey(primaryKeywords[0]?.value)

    try {
      const dataToStore = {
        primary_keywords: primaryKeywords.map((k) => k.value).filter(Boolean),
        secondary_keywords: secondaryKeywords
          .map((k) => k.value)
          .filter(Boolean),
        product_name: productName || "",
        ...(contentFocusAngle.trim() && {
          content_focus_angle: contentFocusAngle.trim(),
        }),
        ...(intentArchetype && { intent_archetype: intentArchetype }),
        ...(contentDepth && { content_depth: contentDepth }),
        ...(notesToAgent.trim() && {
          notes_to_agent: notesToAgent.trim().slice(0, NOTES_TO_AGENT_MAX_LENGTH),
        }),
        ...(competitorsMatching && { competitors_matching: true }),
        outline_type: outlineType || outlineOptions.DEFAULT_OUTLINE,
        plp_url: isPlp ? plpUrl.trim() : undefined,
      }

      await manageKeyVal({
        action: "store",
        key_val: {
          app_name: APP_NAME,
          entity: KeyValEntityType.TASKS_TODO_BUCKET,
          workflow_instance_id: workflowInstanceId,
          key: todoKey,
          data: dataToStore,
        },
      })

      log("info", "Successfully stored form data in key-value store")

      try {
        await triggerWorkflow({
          trigger_workflow: {
            workflow_instance_id: workflowInstanceId,
            data: [
              {
                data: { ...dataToStore, kv_entry_key: todoKey },
                scenario: "default",
              },
            ],
          },
        })
      } catch (triggerError) {
        // Roll back the TODO row so it doesn't get picked up FIFO on a later
        // successful trigger and process a stale submission instead of the
        // user's next attempt.
        log("error", "Manual trigger failed; rolling back TODO row", {
          error: triggerError,
          key: todoKey,
        })
        try {
          await manageKeyVal({
            action: "delete",
            key_val: {
              app_name: APP_NAME,
              entity: KeyValEntityType.TASKS_TODO_BUCKET,
              workflow_instance_id: workflowInstanceId,
              key: todoKey,
            },
          })
        } catch (rollbackError) {
          log("error", "Failed to roll back TODO row after trigger failure", {
            error: rollbackError,
            key: todoKey,
          })
        }
        throw triggerError
      }

      log("info", "Successfully triggered workflow manually")
      toast({
        title: "Success",
        description: `Successfully added ${dataToStore.primary_keywords.length} primary keyword(s). Moving to review screen...`,
        variant: "success",
      })

      navigate(ROUTES.OUTPUT)
    } catch (error) {
      log("error", "Failed to store form data in key-value store", { error })
      toast({
        title: "Error",
        description: "Failed to save data. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="flex min-h-full items-start justify-center p-8">
      <div className="w-full max-w-2xl">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Content Brief Generator
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {screen === 1
              ? "Start with the essentials. You can fine-tune the brief on the next step."
              : "Optional details to sharpen the brief. Skip anything you don't need."}
          </p>
        </div>

        <div className="overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <SectionHeader
            step={screen}
            total={2}
            title={screen === 1 ? "The essentials" : "Fine-tune the brief"}
            subtitle={
              screen === 1
                ? "Required to generate your content brief."
                : "All fields below are optional."
            }
          />

          <div className="flex flex-col gap-6 px-6 py-6">
            {screen === 1 ? (
              <RequiredSection
                outlineType={outlineType}
                onOutlineTypeChange={setOutlineType}
                plpUrl={plpUrl}
                onPlpUrlChange={setPlpUrl}
                plpValid={plpValid}
                isPlp={isPlp}
                primaryKeywords={primaryKeywords}
                onPrimaryKeywordsChange={setPrimaryKeywords}
                secondaryKeywords={secondaryKeywords}
                onSecondaryKeywordsChange={setSecondaryKeywords}
              />
            ) : (
              <OptionalSection
                isDefault={isDefault}
                competitorsMatching={competitorsMatching}
                onCompetitorsMatchingChange={setCompetitorsMatching}
                showContentFocusAngle={showContentFocusAngle}
                productName={productName}
                onProductNameChange={setProductName}
                contentFocusAngle={contentFocusAngle}
                onContentFocusAngleChange={setContentFocusAngle}
                intentArchetype={intentArchetype}
                onIntentArchetypeChange={setIntentArchetype}
                contentDepth={contentDepth}
                onContentDepthChange={setContentDepth}
                notesToAgent={notesToAgent}
                onNotesToAgentChange={setNotesToAgent}
              />
            )}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/30 px-6 py-4">
            {screen === 1 ? (
              <>
                <Button variant="secondary" onClick={handleGoToOutput}>
                  View earlier requests
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    variant="borderless"
                    onClick={() => void handleSubmit()}
                    disabled={!requiredValid || isProcessing}
                  >
                    Skip &amp; generate
                  </Button>
                  <Button
                    onClick={handleContinue}
                    disabled={!requiredValid}
                  >
                    Continue
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setScreen(1)}
                  disabled={isProcessing}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={() => void handleSubmit()}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    "Generate brief"
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export function SectionHeader({
  step,
  total,
  title,
  subtitle,
}: {
  step: number
  total: number
  title: string
  subtitle: string
}) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
      <div className="min-w-0 flex-1">
        <h2 className="truncate text-base font-medium text-foreground">
          {title}
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">{subtitle}</p>
      </div>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        Step {step} of {total}
      </span>
    </div>
  )
}

function FieldLabel({
  title,
  description,
  optional,
}: {
  title: string
  description?: string
  optional?: boolean
}) {
  return (
    <div className="mb-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-foreground">{title}</span>
        {optional && (
          <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
            Optional
          </span>
        )}
      </div>
      {description && (
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  )
}

export type RequiredSectionProps = {
  outlineType: OutlineType
  onOutlineTypeChange: (v: OutlineType) => void
  plpUrl: string
  onPlpUrlChange: (v: string) => void
  plpValid: boolean
  isPlp: boolean
  primaryKeywords: TagInputItem[]
  onPrimaryKeywordsChange: (v: TagInputItem[]) => void
  secondaryKeywords: TagInputItem[]
  onSecondaryKeywordsChange: (v: TagInputItem[]) => void
}

export function RequiredSection({
  outlineType,
  onOutlineTypeChange,
  plpUrl,
  onPlpUrlChange,
  plpValid,
  isPlp,
  primaryKeywords,
  onPrimaryKeywordsChange,
  secondaryKeywords,
  onSecondaryKeywordsChange,
}: RequiredSectionProps) {
  return (
    <>
      <div>
        <FieldLabel
          title="What kind of outline are you creating?"
          description="We'll tailor the rest of the questions based on your choice."
        />
        <ChoiceOptions
          options={outlineChoices}
          value={outlineType}
          onChange={(id) => onOutlineTypeChange(id as OutlineType)}
          autoFocus={false}
        />
      </div>

      {isPlp && (
        <div>
          <FieldLabel
            title="Product listing page URL"
            description="Paste the URL of the PLP you want this outline tied to."
          />
          <input
            type="url"
            value={plpUrl}
            onChange={(e) => onPlpUrlChange(e.target.value)}
            placeholder="https://example.com/products/…"
            aria-invalid={plpUrl.trim().length > 0 && !plpValid}
            className={inputClass}
          />
        </div>
      )}

      <div>
        <FieldLabel
          title="Primary keywords"
          description="Add at least one keyword. Press Enter after each one."
        />
        <TagInput
          tags={primaryKeywords}
          onTagsChange={onPrimaryKeywordsChange}
          placeholder="Add keyword"
          emptyStateText="No keywords added yet."
          tagsLabelText="Primary keywords:"
          minTags={1}
          autoComplete="on"
          name="primary_keywords"
          id="primary_keywords"
        />
      </div>

      <div>
        <FieldLabel
          title="Secondary keywords"
          description="Helpful for broader topical coverage."
          optional
        />
        <TagInput
          tags={secondaryKeywords}
          onTagsChange={onSecondaryKeywordsChange}
          placeholder="Add keyword"
          emptyStateText="No keywords added yet."
          tagsLabelText="Secondary keywords:"
          autoComplete="on"
          name="secondary_keywords"
          id="secondary_keywords"
        />
      </div>
    </>
  )
}

export type OptionalSectionProps = {
  isDefault: boolean
  competitorsMatching: boolean
  onCompetitorsMatchingChange: (v: boolean) => void
  showContentFocusAngle: boolean
  productName: string
  onProductNameChange: (v: string) => void
  contentFocusAngle: string
  onContentFocusAngleChange: (v: string) => void
  intentArchetype: string
  onIntentArchetypeChange: (v: string) => void
  contentDepth: string
  onContentDepthChange: (v: string) => void
  notesToAgent: string
  onNotesToAgentChange: (v: string) => void
}

export function OptionalSection({
  isDefault,
  competitorsMatching,
  onCompetitorsMatchingChange,
  showContentFocusAngle,
  productName,
  onProductNameChange,
  contentFocusAngle,
  onContentFocusAngleChange,
  intentArchetype,
  onIntentArchetypeChange,
  contentDepth,
  onContentDepthChange,
  notesToAgent,
  onNotesToAgentChange,
}: OptionalSectionProps) {
  const hasNoOptionalFields = !isDefault && !showContentFocusAngle

  if (hasNoOptionalFields) {
    return (
      <div className="flex flex-col items-center gap-1 py-6 text-center">
        <p className="text-sm font-medium text-foreground">
          Nothing else to configure for PLP outlines
        </p>
        <p className="max-w-md text-xs text-muted-foreground">
          Product listing page outlines are derived from the PLP URL you
          provided. Hit Generate brief to continue.
        </p>
      </div>
    )
  }

  return (
    <>
      {isDefault && (
        <div>
          <FieldLabel
            title="Product or service name"
            description="Used for context when generating sections."
          />
          <input
            type="text"
            value={productName}
            onChange={(e) => onProductNameChange(e.target.value)}
            placeholder="E.g., Stripe Payment Links"
            className={inputClass}
          />
        </div>
      )}

      {showContentFocusAngle && (
        <div>
          <FieldLabel
            title="Content focus angle"
            description="E.g., focus on technical implementation, ROI for enterprise, ease of use…"
          />
          <TextArea
            value={contentFocusAngle}
            onChange={(e) => onContentFocusAngleChange(e.target.value)}
            placeholder="E.g., Focus on ROI for enterprise customers, emphasize ease of use for beginners…"
            rows={4}
            className="resize-none"
          />
        </div>
      )}

      {isDefault && (
        <div className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3">
          <div className="min-w-0 flex-1 space-y-0.5 pr-3">
            <label
              htmlFor="competitors_matching"
              className="text-sm font-medium"
            >
              Match competitor patterns
            </label>
            <p className="text-xs text-muted-foreground">
              Derive intent archetype, depth, and goal from competitor analysis.
            </p>
          </div>
          <Switch
            id="competitors_matching"
            checked={competitorsMatching}
            onCheckedChange={onCompetitorsMatchingChange}
          />
        </div>
      )}

      {isDefault && !competitorsMatching && (
        <>
          <div>
            <FieldLabel
              title="Intent archetype"
              description="Controls tone and whether product-oriented sections appear."
            />
            <ChoiceOptions
              options={intentArchetypeOptions}
              value={intentArchetype}
              onChange={onIntentArchetypeChange}
              autoFocus={false}
            />
          </div>

          <div>
            <FieldLabel
              title="Content depth"
              description="Controls number of H2 sections and target word count."
            />
            <ChoiceOptions
              options={contentDepthOptions}
              value={contentDepth}
              onChange={onContentDepthChange}
              autoFocus={false}
            />
          </div>

          <div>
            <FieldLabel
              title="Notes to agent"
              description="Anything else the brief should know: goals, topics to cover or skip, product info, etc."
            />
            <TextArea
              value={notesToAgent}
              onChange={(e) =>
                onNotesToAgentChange(
                  e.target.value.slice(0, NOTES_TO_AGENT_MAX_LENGTH),
                )
              }
              placeholder="E.g., Reader should understand the difference between deductibles and copays. Cover claim denials, skip anything about international coverage."
              rows={4}
              className="resize-none"
              maxLength={NOTES_TO_AGENT_MAX_LENGTH}
            />
            <p className="mt-1 text-right text-xs text-muted-foreground">
              {notesToAgent.length}/{NOTES_TO_AGENT_MAX_LENGTH}
            </p>
          </div>
        </>
      )}
    </>
  )
}
